import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const api = () => (process.env.REACT_APP_API_URL || "").replace(/\/?$/, "/");

const WhatsAppBookingForm = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [ready, setReady] = useState(false);
  const [form, setForm] = useState({
    title: "Booking details",
    kind: "sender",
    name: "",
    phone: "",
    address: "",
    cityId: "",
    cityName: "",
    cities: [],
  });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(`${api()}WhatsAppForm/${token}`);
        if (cancelled) return;
        setForm({
          title: data.title || data.Title || "Booking details",
          kind: data.kind || data.Kind || "sender",
          name: data.name || data.Name || "",
          phone: data.phone || data.Phone || "",
          address: data.address || data.Address || "",
          cityId: data.cityId || data.CityId || "",
          cityName: data.cityName || data.CityName || "",
          cities: Array.isArray(data.cities)
            ? data.cities
            : Array.isArray(data.Cities)
              ? data.Cities
              : [],
        });
        setReady(true);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              "This form link is invalid or has expired.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (token) run();
    else {
      setLoading(false);
      setError("Missing form token.");
    }
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post(`${api()}WhatsAppForm/${token}`, {
        name: form.name,
        phone: form.phone,
        address: form.address,
        cityId: form.cityId ? Number(form.cityId) : null,
        cityName: form.cityName,
      });
      setDone(data?.message || "Thank you — you can return to WhatsApp.");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Could not save the details. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-stone-500">
        Loading form…
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-md mx-auto my-10 rounded-2xl border border-stone-200 bg-white p-6">
        <h1 className="text-xl font-semibold text-orange-700">Thank you</h1>
        <p className="mt-3 text-stone-600">{done}</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto my-10 rounded-2xl border border-stone-200 bg-white p-6">
      <h1 className="text-xl font-semibold text-orange-700">{form.title}</h1>
      <p className="mt-2 text-stone-600">
        Please fill this in once. We will use it for your Sundarban Cargo
        booking.
      </p>
      {error ? (
        <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-red-700">{error}</p>
      ) : null}
      {ready ? (
        <form className="mt-4 space-y-4" onSubmit={submit}>
          <label className="block">
            <span className="text-sm font-semibold">Name</span>
            <input
              required
              className="mt-1 w-full rounded-xl border border-stone-300 px-3 h-11"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold">
              Phone{form.kind === "receiver" ? "" : " (optional)"}
            </span>
            <input
              required={form.kind === "receiver"}
              className="mt-1 w-full rounded-xl border border-stone-300 px-3 h-11"
              value={form.phone}
              onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold">City</span>
            <select
              className="mt-1 w-full rounded-xl border border-stone-300 px-3 h-11"
              value={form.cityId}
              onChange={(e) => setForm((prev) => ({ ...prev, cityId: e.target.value }))}
            >
              <option value="">Select city</option>
              {form.cities.map((city) => (
                <option key={city.id ?? city.Id} value={city.id ?? city.Id}>
                  {city.name || city.Name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-semibold">City if not listed</span>
            <input
              className="mt-1 w-full rounded-xl border border-stone-300 px-3 h-11"
              value={form.cityName}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, cityName: e.target.value }))
              }
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold">Address</span>
            <textarea
              required
              rows={3}
              className="mt-1 w-full rounded-xl border border-stone-300 px-3 py-2"
              value={form.address}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, address: e.target.value }))
              }
            />
          </label>
          <button
            type="submit"
            disabled={saving}
            className="w-full rounded-xl bg-orange-600 text-white font-semibold h-11 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Submit details"}
          </button>
        </form>
      ) : null}
    </div>
  );
};

export default WhatsAppBookingForm;
